import React from 'react'
import PropTypes from 'prop-types'
import { kebabCase } from 'lodash'
import _cx from 'classnames/bind'
import { enums } from '@adc/sdk'
import { Text, utils as coreUtils } from '@adc/ui-core'
import cls from './styles.scss'

const cx = _cx.bind(cls)
const formatMoney = coreUtils.formatters.formatMoney

const YOUR_WINNING_BID_LABEL = 'Your Winning Bid'
const YOUR_BID_LABEL = 'Your Next Bid'
const BUYERS_PREMIUM_LABEL = "Buyer's Premium"
const TOTAL_PURCHASE_PRICE_LABEL = 'Total Purchase Price'
const EARNEST_MONEY_DEPOSIT = 'Earnest Money Deposit'
const SALES_DEPOSIT = 'Sales Deposit'
const YOUR_HIGHEST_BID_LABEL = 'Your Highest Bid'
const YOUR_PREVIOUS_BID_LABEL = 'Your Previous Bid'
const MATCH_BID_LABEL = 'Match Bid'

export const CostView = ({
  elmId,
  hasError,
  label,
  labelColor = 'gray',
  labelWeight,
  operator,
  value,
  valueBackground,
  valueColor = 'gray',
  valueExplanation,
  valueWeight,
  tooltip,
}) => {
  return (
    <div className={cx('cost-view')} data-elm-id={elmId}>
      <div>
        <Text
          as='span'
          color={labelColor}
          data-elm-id='label'
          weight={labelWeight}
        >
          {label}
        </Text>
        {tooltip}
        {valueExplanation && (
          <Text
            className={cx('u-pt-1', 'u-pr-2', 'u-pb-2')}
            color='grayMedium'
            data-elm-id='explanation'
            size='tiny'
          >
            {valueExplanation}
          </Text>
        )}
      </div>
      <div>
        {operator && !hasError && (
          <Text className={cx('u-mr-1')} as='span' color='grayMedium'>
            {operator}
          </Text>
        )}
        <Text
          className={cx('value', kebabCase(valueBackground))}
          as='span'
          color={valueColor}
          data-elm-id='value'
          weight={valueWeight}
        >
          {hasError ? '–' : formatMoney(value)}
        </Text>
      </div>
    </div>
  )
}

CostView.propTypes = {
  elmId: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelColor: Text.propTypes.color,
  labelWeight: Text.propTypes.weight,
  operator: PropTypes.string,
  value: PropTypes.number.isRequired,
  valueBackground: PropTypes.oneOf(['grayLight', 'grayExtraLight']),
  valueColor: Text.propTypes.color,
  valueExplanation: PropTypes.string,
  valueWeight: Text.propTypes.weight,
  tooltip: PropTypes.any,
}

export const YourBid = props => {
  return (
    <CostView
      elmId='cost_calculation_your_bid'
      label={YOUR_BID_LABEL}
      {...props}
    />
  )
}

export const BuyersPremium = props => {
  return (
    <CostView
      elmId='cost_calculation_buyers_premium'
      label={BUYERS_PREMIUM_LABEL}
      {...props}
    />
  )
}

export const TotalPurchasePrice = props => {
  return (
    <CostView
      elmId='cost_calculation_total_purchase_price'
      label={TOTAL_PURCHASE_PRICE_LABEL}
      {...props}
    />
  )
}

export const EarnestMoneyDeposit = props => {
  return (
    <CostView
      elmId='cost_calculation_earnest_money_deposit'
      label={EARNEST_MONEY_DEPOSIT}
      {...props}
    />
  )
}

export const SalesDeposit = props => {
  return (
    <CostView
      elmId='cost_calculation_sales_deposit'
      label={SALES_DEPOSIT}
      {...props}
    />
  )
}

export const YourWinningBid = props => {
  return (
    <CostView
      elmId='cost_calculation_winning_bid'
      label={YOUR_WINNING_BID_LABEL}
      {...props}
    />
  )
}

export const YourHighestBid = props => {
  const value = !props.value ? '' : props.value
  return (
    <CostView
      elmId='cost_calculation_your_highest_bid'
      label={YOUR_HIGHEST_BID_LABEL}
      {...props}
      value={value}
    />
  )
}

export const ConfirmingBid = props => {
  const {
    bidAmount,
    buyersPremium = null,
    totalPurchasePrice = null,
    earnestMoneyDeposit = null,
    isHighestBid = false,
  } = props
  return (
    <div
      data-elm-id='confirm_bid_calculations'
      className={cx('contains-emphasis')}
    >
      <div
        className={
          earnestMoneyDeposit !== null
            ? cx('border-bottom', 'margin-bottom')
            : null
        }
      >
        {bidAmount !== null &&
          (isHighestBid ? (
            <YourHighestBid value={bidAmount} />
          ) : (
            <YourBid value={bidAmount} />
          ))}
        {buyersPremium !== null && (
          <BuyersPremium operator='+' value={buyersPremium} />
        )}
        {totalPurchasePrice !== null && (
          <TotalPurchasePrice
            labelWeight='heavy'
            operator='='
            value={totalPurchasePrice ?? 0}
            valueBackground='grayExtraLight'
            valueWeight='heavy'
          />
        )}
      </div>
      {earnestMoneyDeposit !== null && (
        <EarnestMoneyDeposit
          labelColor='grayMedium'
          value={earnestMoneyDeposit}
          valueColor='grayMedium'
        />
      )}
    </div>
  )
}

ConfirmingBid.propTypes = {
  bidAmount: PropTypes.number.isRequired,
  buyersPremium: PropTypes.number,
  totalPurchasePrice: PropTypes.number,
  earnestMoneyDeposit: PropTypes.number,
  isHighestBid: PropTypes.bool,
}

export const TpsConfirmingBid = ({
  salesDeposit,
  bidAmount,
  segment,
  type,
}) => {
  let requiredStateDepositAmount
  let stateDepositRule

  if (segment.__methods__?.calculateProposedBidBreakdown) {
    const {
      requiredStateDepositAmount: requiredAmount = bidAmount,
      stateDepositRule: rule = '100%',
    } = segment.__methods__.calculateProposedBidBreakdown(bidAmount) || {}

    requiredStateDepositAmount = requiredAmount
    stateDepositRule = rule
  } else {
    requiredStateDepositAmount = bidAmount
    stateDepositRule = '100%'
  }

  const { shouldShowStateDeposit } =
    segment.__methods__?.getStateDepositValues?.() || {}
  const stateDepositText = shouldShowStateDeposit
    ? segment.__methods__?.getStateDepositText?.(requiredStateDepositAmount)
    : null

  if (type === enums.OnlineAuctionSegmentBidType.Regular && salesDeposit)
    return salesDeposit ? (
      <div
        data-elm-id='confirm_bid_calculations'
        className={cx('contains-emphasis')}
      >
        <SalesDeposit
          labelColor='grayMedium'
          value={salesDeposit}
          valueColor='grayMedium'
        />
      </div>
    ) : null
  else if (type === enums.LiveAuctionSegmentBidType.RemoteBid)
    return stateDepositText?.title ? (
      <Text
        className='u-mb-2'
        size='tiny'
        color='gray'
        elmId='bid_deposit_breakdown'
      >
        {stateDepositText.title}
      </Text>
    ) : null
  else
    return (
      <Text className='u-mb-2' size='tiny' color='gray'>
        {`A deposit of ${formatMoney(
          requiredStateDepositAmount
        )} (${stateDepositRule}) is required in your funds at the end of the auction in order to win.`}
      </Text>
    )
}

TpsConfirmingBid.propTypes = {
  salesDeposit: PropTypes.number,
  bidAmount: PropTypes.number.isRequired,
  segment: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
}

const calculationPropTypes = {
  value: PropTypes.number,
}

YourBid.propTypes =
  BuyersPremium.propTypes =
  TotalPurchasePrice.propTypes =
  EarnestMoneyDeposit.propTypes =
  YourWinningBid.propTypes =
  YourHighestBid.propTypes =
    calculationPropTypes

const YourPreviousBid = props => {
  return (
    <CostView
      elmId='cost_calculation_your_bid'
      label={YOUR_PREVIOUS_BID_LABEL}
      {...props}
    />
  )
}
const MatchBid = props => {
  return (
    <CostView
      elmId='cost_calculation_your_bid'
      label={MATCH_BID_LABEL}
      {...props}
    />
  )
}

export const MatchBidCosts = ({
  currentBidAmount,
  currentUserBidAmount,
  buyersPremium,
  totalPurchasePrice,
  earnestMoneyDeposit,
}) => {
  return (
    <div
      data-elm-id='confirm_bid_calculations'
      className={cx('contains-emphasis', 'margin-bottom')}
    >
      <div className={cx('margin-bottom')}>
        <YourPreviousBid value={currentUserBidAmount} />
      </div>
      <div className={cx('border-top', 'border-bottom', 'margin-bottom')}>
        <MatchBid value={currentBidAmount} />
        <BuyersPremium operator='+' value={buyersPremium} />
        <TotalPurchasePrice
          labelWeight='heavy'
          operator='='
          value={totalPurchasePrice}
          valueBackground='grayExtraLight'
          valueWeight='heavy'
        />
      </div>
      <EarnestMoneyDeposit
        labelColor='grayMedium'
        value={earnestMoneyDeposit}
        valueColor='grayMedium'
      />
    </div>
  )
}

MatchBidCosts.propTypes = {
  currentBidAmount: PropTypes.number,
  currentUserBidAmount: PropTypes.number,
  buyersPremium: PropTypes.number,
  totalPurchasePrice: PropTypes.number,
  earnestMoneyDeposit: PropTypes.number,
}
